import { useEffect, useState, useRef } from "react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Image as ImageIcon, Upload, Loader2, Heart } from "lucide-react";

export default function AdminGallery() {
  usePageMeta("Happy Customers");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [imageId, setImageId] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    api.get("/admin/gallery").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setImageId(null); setCaption(""); setOpen(true); };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageId(data.image_id);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!imageId) { toast.error("Please upload a photo first"); return; }
    setSaving(true);
    try {
      await api.post("/admin/gallery", { image_id: imageId, caption });
      toast.success("Added to Happy Customers");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm("Remove this photo?")) return;
    try { await api.delete(`/admin/gallery/${item.id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-ink sm:text-3xl">Happy Customers</h1>
          <p className="mt-1 text-ink-soft">Share customer photos — shown on the homepage. Only you can add these.</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-brand font-semibold text-ink hover:bg-brand-hover" data-testid="add-gallery-btn">
          <Plus className="mr-1 h-4 w-4" /> Add Photo
        </Button>
      </div>

      {loading ? (
        <div className="mt-10 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
      ) : items.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-border bg-white p-12 text-center">
          <Heart className="mx-auto h-10 w-10 text-ink-muted" />
          <p className="mt-4 font-heading text-lg text-ink">No customer photos yet</p>
          <p className="text-sm text-ink-muted">Add happy customer photos to build trust on your homepage.</p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((item) => (
            <div key={item.id} className="group relative overflow-hidden rounded-2xl border border-border/60 bg-white shadow-ambient" data-testid={`gallery-item-${item.id}`}>
              <div className="aspect-square w-full overflow-hidden bg-cream-alt">
                <img src={fileUrl(item.image_id)} alt={item.caption || "Happy customer"} className="h-full w-full object-cover" />
              </div>
              {item.caption && <p className="p-3 text-sm text-ink-soft line-clamp-2">{item.caption}</p>}
              <button onClick={() => remove(item)} className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-destructive opacity-0 shadow transition-opacity group-hover:opacity-100" data-testid={`delete-gallery-${item.id}`} aria-label="Remove">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Add Customer Photo</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="h-24 w-24 shrink-0 overflow-hidden rounded-xl bg-cream-alt">
                {imageId ? <img src={fileUrl(imageId)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-ink-muted"><ImageIcon className="h-6 w-6" /></div>}
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="gallery-image-input" />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full">
                  {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  {imageId ? "Change photo" : "Upload photo"}
                </Button>
                <p className="mt-1 text-xs text-ink-muted">JPG, PNG or WEBP · max 5MB</p>
              </div>
            </div>
            <div>
              <Label>Caption (optional)</Label>
              <Input value={caption} onChange={(e) => setCaption(e.target.value)} className="mt-1.5 rounded-xl" placeholder="e.g. Priya from Machavaram loves our eggs!" data-testid="gallery-caption-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-full bg-brand font-semibold text-ink" data-testid="save-gallery-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
